import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLocale } from "@/hooks/useLocale";
import { localizedPath, isHomePathname } from "@/i18n/languages";
import { MAIN_SITE_URL } from "@/constants";
import { categories } from "@/mocks/categories";

const SECTION_ITEMS = [
  { id: "categories", key: "nav.categories" },
  { id: "guides", key: "nav.shoppingGuide" },
  { id: "about", key: "nav.about" },
  { id: "faq", key: "nav.faq" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const lang = useLocale();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = isHomePathname(location.pathname);

  const sectionHref = (id: string) =>
    onHome ? `#${id}` : `${localizedPath(lang, "/")}#${id}`;

  const homeHref = localizedPath(lang, "/");

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-background-200 bg-background-50">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link
          to={homeHref}
          className="flex items-center gap-2 whitespace-nowrap"
          onClick={() => setMenuOpen(false)}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500">
            <i className="ri-table-2 text-lg text-white" />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
            Basetao Sheet
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          <Link
            to={homeHref}
            className="whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:text-primary-600"
          >
            {t("nav.home")}
          </Link>
          {SECTION_ITEMS.map((item) => (
            <a
              key={item.id}
              href={sectionHref(item.id)}
              className="whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:text-primary-600"
            >
              {t(item.key)}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher scrolled />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            {t("nav.cta")}
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-900 md:hidden"
            aria-label="Menu"
          >
            <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to={homeHref}
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                <i className="ri-home-4-line text-base" />
              </span>
              {t("nav.home")}
            </Link>
            {SECTION_ITEMS.map((item) => (
              <a
                key={item.id}
                href={sectionHref(item.id)}
                onClick={() => setMenuOpen(false)}
                className="whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
              >
                {t(item.key)}
              </a>
            ))}

            <div className="mt-3 border-t border-background-200 pt-3">
              <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                {t("nav.categories")}
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-between gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
                  >
                    <span className="truncate">{t(`cat.${cat.id}`)}</span>
                    <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-foreground-400">
                      <i className="ri-arrow-right-up-line text-sm" />
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-center text-sm font-semibold text-background-50"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}